import { Component, signal, computed, OnInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { validateAndFocusFirstError } from '../../utils/form-validation';
import { friendlyError } from '../../utils/friendly-error';
import { formatClockTime } from '../../utils/format';
import { SalaService } from '../../services/sala.service';
import { ReservationService } from '../../services/reservation.service';
import { CustomerService } from '../../services/customer.service';
import { AuthService } from '../../services/auth.service';
import { SpaceScheduleService } from '../../services/space-schedule.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { Reserva, CalendarResponse, Reservation, Sala, CreateReservationForUserRequest, CreateReservationForGuestRequest, SpaceSchedule } from '../../models/sala.model';
import { Observable, of, map } from 'rxjs';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';

// Tipo para las reservas del calendario que usan 'date' en lugar de 'reservation_date'
type CalendarReservation = Reservation & { date: string };

@Component({
  selector: 'app-calendario',
  standalone: true,
  imports: [CommonModule, FormsModule, DateTimePickerDirective, CooldownBubbleDirective],
  template: `
    <div class="calendario-container">
      <div class="calendario-header">
        <div class="view-controls">
          <div class="view-buttons">
            <button 
              (click)="setView('dia')" 
              [class.active]="currentView() === 'dia'"
              class="view-btn"
            >
              Día
            </button>
            <button 
              (click)="setView('semana')" 
              [class.active]="currentView() === 'semana'"
              class="view-btn"
            >
              Semana
            </button>
            <button 
              (click)="setView('mes')" 
              [class.active]="currentView() === 'mes'"
              class="view-btn"
            >
              Mes
            </button>
          </div>
          <div class="date-navigation">
            <button (click)="previousPeriod()" class="nav-btn">
              ← {{ getPreviousText() }}
            </button>
            <span class="current-date">{{ getCurrentDateText() }}</span>
            <button (click)="nextPeriod()" class="nav-btn">
              {{ getNextText() }} →
            </button>
            <button (click)="goToToday()" class="today-btn">
              Hoy
            </button>
          </div>
        </div>
      </div>

      <!-- Vista de día -->
      <div *ngIf="currentView() === 'dia'" class="calendar-grid">
        <div class="time-column">
          <div class="time-header">Hora</div>
          <div *ngFor="let hour of horas" class="time-slot">
            {{ formatHour(hour) }}
          </div>
        </div>
        
        <div class="salas-columns">
          <div *ngFor="let sala of salas()" class="sala-column">
            <div class="sala-header" [title]="sala.nombre">
              <h3>{{ sala.nombre }}</h3>
              <span class="sala-capacity">{{ sala.capacidad }} personas</span>
            </div>
            
            <div class="sala-slots">
              <div 
                *ngFor="let hour of horas" 
                class="time-slot"
                [class.reserved]="getReservaForSalaAndHour(sala.id, hour)"
                [class.confirmed]="getReservaForSalaAndHour(sala.id, hour)?.status === 'confirmed'"
                [class.pending]="getReservaForSalaAndHour(sala.id, hour)?.status === 'pending'"
                [class.cancelled]="getReservaForSalaAndHour(sala.id, hour)?.status === 'cancelled'"
                [class.completed]="getReservaForSalaAndHour(sala.id, hour)?.status === 'completed'"
                [class.available]="!getReservaForSalaAndHour(sala.id, hour)"
                (click)="handleTimeSlotClick(sala.id, hour, currentDate())"
              >
                <div *ngIf="getReservaForSalaAndHour(sala.id, hour)" class="reserva-info">
                  <div class="reserva-cliente">
                    {{ getReservaForSalaAndHour(sala.id, hour)?.customer_name }}
                  </div>
                  <div class="reserva-time">
                    {{ formatClockTime(getReservaForSalaAndHour(sala.id, hour)?.start_time) }} -
                    {{ formatClockTime(getReservaForSalaAndHour(sala.id, hour)?.end_time) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vista de semana -->
      <div *ngIf="currentView() === 'semana'" class="week-grid">
        <div class="week-header">
          <!-- Reserva el mismo ancho que .time-column-week de la fila de
               abajo (80px) -- sin esto, las 7 columnas del encabezado no
               quedaban alineadas con sus columnas de día reales. -->
          <div class="week-header-spacer" aria-hidden="true"></div>
          <div class="day-header" *ngFor="let day of weekDays()">
            <div class="day-name">{{ day.name }}</div>
            <div class="day-date">{{ day.date }}</div>
          </div>
        </div>
        
        <div class="week-content">
          <div class="time-column-week">
            <div class="time-header">Hora</div>
            <div *ngFor="let hour of horas" class="time-slot">
              {{ formatHour(hour) }}
            </div>
          </div>
          
          <div class="week-days">
            <div *ngFor="let day of weekDays()" class="day-column">
              <div *ngFor="let sala of salas()" class="sala-week-column">
                <div class="sala-week-header">{{ sala.nombre }}</div>
                <div class="sala-week-slots">
                  <div 
                    *ngFor="let hour of horas" 
                    class="time-slot"
                    [class.reserved]="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)"
                    [class.confirmed]="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.status === 'confirmed'"
                    [class.pending]="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.status === 'pending'"
                    [class.cancelled]="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.status === 'cancelled'"
                    [class.completed]="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.status === 'completed'"
                    [class.available]="!getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)"
                    (click)="handleTimeSlotClick(sala.id, hour, day.dateObj)"
                  >
                    <div *ngIf="getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)" class="reserva-info">
                      <div class="reserva-cliente">
                        {{ getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.customer_name }}
                      </div>
                      <div class="reserva-time">
                        {{ formatClockTime(getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.start_time) }} -
                        {{ formatClockTime(getReservaForSalaAndHourWeek(sala.id, hour, day.dateObj)?.end_time) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vista de mes -->
      <div *ngIf="currentView() === 'mes'" class="month-grid">
        <div class="month-header">
          <div class="day-name" *ngFor="let dayName of monthDayNames">{{ dayName }}</div>
        </div>
        
        <div class="month-content">
          <div *ngFor="let week of monthWeeks()" class="month-week">
            <div *ngFor="let day of week" class="month-day" [class.other-month]="!day.isCurrentMonth">
              <div class="day-number">{{ day.day }}</div>
              <div class="day-reservas">
                <div 
                  *ngFor="let reserva of getReservasForDay(day.dateObj)" 
                  class="reserva-month"
                  [class.confirmed]="reserva.status === 'confirmed'"
                  [class.pending]="reserva.status === 'pending'"
                  [class.cancelled]="reserva.status === 'cancelled'"
                  [class.completed]="reserva.status === 'completed'"
                  (click)="showReservaDetails(reserva)"
                >
                  <div class="reserva-time-month">
                    {{ formatClockTime(reserva.start_time) }}-{{ formatClockTime(reserva.end_time) }}
                  </div>
                  <div class="reserva-sala-month">{{ reserva.space_title }}</div>
                  <div class="reserva-cliente-month">{{ reserva.customer_name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de detalles de reserva -->
      <div *ngIf="selectedReserva()" class="modal-overlay dark-overlay" (click)="closeModal()">
        <div class="modal-content" (click)="$event.stopPropagation()">
          <div class="modal-header dark-header">
            <h3>Detalles de la Reserva</h3>
            <button (click)="closeModal()" class="close-btn">×</button>
          </div>
          
          <div class="modal-body">
            <div class="reserva-detail">
              <strong>Sala:</strong> {{ selectedReserva()?.space_title }}
            </div>
            <div class="reserva-detail">
              <strong>Fecha:</strong> {{ getReservaFecha() }}
            </div>
            <div class="reserva-detail">
              <strong>Horario:</strong> 
              {{ formatClockTime(selectedReserva()?.start_time) }} - {{ formatClockTime(selectedReserva()?.end_time) }}
            </div>
            <div class="reserva-detail">
              <strong>Cliente:</strong> {{ selectedReserva()?.customer_name }}
            </div>
            <div class="reserva-detail">
              <strong>Teléfono:</strong> {{ selectedReserva()?.customer_phone || selectedReserva()?.guest_phone }}
            </div>
            <div class="reserva-detail">
              <strong>Email:</strong> {{ selectedReserva()?.customer_email }}
            </div>
            <div class="reserva-detail">
              <strong>Estado:</strong> 
              <span [class]="'status-' + selectedReserva()?.status">
                {{ getEstadoText(selectedReserva()?.status || '') }}
              </span>
            </div>
            <div class="reserva-detail">
              <strong>Precio Total:</strong> $ {{ selectedReserva()?.total_price }}
            </div>
            <div class="modal-actions">
              <button type="button" class="btn-secondary" (click)="closeModal()">Cerrar</button>
              <button type="button" class="btn-primary" (click)="onEditFromDetail()">Editar</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de nueva reserva -->
      <div *ngIf="showNewReservaModal()" class="modal-overlay" (click)="closeNewReservaModal()">
        <div class="modal-content-large new-reservation-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3>Nueva Reserva</h3>
            <button (click)="closeNewReservaModal()" class="close-btn">×</button>
          </div>
          
          <div class="modal-body">
            <!-- Información prellenada -->
            <div class="info-section">
              <div class="info-item">
                <strong>Espacio:</strong> {{ getEspacioNombre(selectedSpaceId()) }}
              </div>
              <div class="info-item" *ngIf="!multiDateMode">
                <strong>Fecha:</strong> {{ formatDate(selectedDate()) }}
              </div>
              <div class="info-item" *ngIf="multiDateMode">
                <strong>Días:</strong> {{ selectedDates.length }} seleccionado(s)
              </div>
              <div class="info-item">
                <strong>Horario seleccionado:</strong> {{ newReservaForm.start_time ? formatClockTime(newReservaForm.start_time) : '--:--' }} - {{ newReservaForm.end_time ? formatClockTime(newReservaForm.end_time) : '--:--' }}
              </div>
            </div>

            <!-- Reserva multi-día: mismo espacio y horario aplicado a varios
                 días habilitados del espacio, en una sola operación con un
                 solo pago y comprobante. -->
            <label class="multi-date-toggle">
              <input
                type="checkbox"
                [(ngModel)]="multiDateMode"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onMultiDateModeChange()"
              />
              <span>Reservar varios días (mismo horario, un solo pago)</span>
            </label>

            <div class="form-group" *ngIf="multiDateMode">
              <label for="multiDates">Días a reservar *</label>
              <input
                id="multiDates"
                type="text"
                [ngModel]="multiDatesInputValue"
                [ngModelOptions]="{standalone: true}"
                appDatePicker="date"
                [multiple]="true"
                [availableDays]="multiDateAvailableDays"
                [minDate]="minDateStr"
                [pickerDisabled]="!selectedSpaceId()"
                (datesValueChange)="onMultiDatesChange($event)"
                class="form-control"
                placeholder="Seleccionar días"
              />
              <div *ngIf="multiDateScheduleError" class="error-msg">{{ multiDateScheduleError }}</div>
            </div>

            <!-- Selector de tipo de reservación -->
            <div class="form-group">
              <label>Tipo de Reservación *</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input 
                    type="radio" 
                    name="reservationType" 
                    value="user"
                    [(ngModel)]="reservationType"
                    (change)="onReservationTypeChange()"
                  />
                  <span>Usuario Registrado</span>
                </label>
                <label class="radio-label">
                  <input 
                    type="radio" 
                    name="reservationType" 
                    value="guest"
                    [(ngModel)]="reservationType"
                    (change)="onReservationTypeChange()"
                  />
                  <span>Invitado</span>
                </label>
              </div>
            </div>

<form #reservaForm="ngForm" #reservaFormEl (ngSubmit)="createReserva()" class="reserva-form">
               <div class="form-row" *ngIf="!multiDateMode">
                 <div class="form-group">
                   <label for="startTime">Hora de Inicio *</label>
                   <select
                     id="startTime"
                     #startTimeCtrl="ngModel"
                     [(ngModel)]="newReservaForm.start_time"
                     name="startTime"
                     required
                     class="form-control"
                     [class.invalid]="startTimeCtrl.invalid && (startTimeCtrl.dirty || startTimeCtrl.touched)"
                     (ngModelChange)="onStartTimeChange()"
                   >
                     <option value="">Seleccionar hora de inicio</option>
                     <option *ngFor="let time of availableStartTimes" [value]="time">{{ formatClockTime(time) }}</option>
                   </select>
                   <div *ngIf="startTimeCtrl.invalid && (startTimeCtrl.dirty || startTimeCtrl.touched)" class="error-msg">
                     Debes seleccionar una hora de inicio
                   </div>
                 </div>
                 <div *ngIf="isLoadingTimeOptions()" class="info-item">Cargando horarios disponibles...</div>
                 <div *ngIf="!isLoadingTimeOptions() && availableStartTimes.length === 0" class="error-msg">
                   No hay horarios habilitados para reservar en esta fecha.
                 </div>

                 <div class="form-group">
                   <label for="endTime">Hora de Fin *</label>
                   <select
                     id="endTime"
                     #endTimeCtrl="ngModel"
                     [(ngModel)]="newReservaForm.end_time"
                     name="endTime"
                     required
                     class="form-control"
                     [class.invalid]="endTimeCtrl.invalid && (endTimeCtrl.dirty || endTimeCtrl.touched)"
                   >
                     <option value="">Seleccionar hora de fin</option>
                     <option *ngFor="let time of availableEndTimes" [value]="time">{{ formatClockTime(time) }}</option>
                   </select>
                   <div *ngIf="endTimeCtrl.invalid && (endTimeCtrl.dirty || endTimeCtrl.touched)" class="error-msg">
                     Debes seleccionar una hora de fin
                   </div>
                 </div>
               </div>

               <!-- Reserva multi-día: sin selects filtrados por choques de UN
                    solo día (no aplican con varias fechas a la vez) — el
                    rango de hora se valida contra la intersección de horario
                    de los días elegidos; los choques puntuales los valida el
                    backend al crear (todo o nada). -->
               <div class="form-row" *ngIf="multiDateMode">
                 <div class="form-group">
                   <label for="startTimeMulti">Hora de Inicio *</label>
                   <input
                     type="text"
                     id="startTimeMulti"
                     #startTimeMultiCtrl="ngModel"
                     [(ngModel)]="newReservaForm.start_time"
                     name="startTimeMulti"
                     required
                     class="form-control"
                     [class.invalid]="startTimeMultiCtrl.invalid && (startTimeMultiCtrl.dirty || startTimeMultiCtrl.touched)"
                     appDatePicker="time"
                     [minTime]="multiDateMinTime"
                     [maxTime]="multiDateMaxTime"
                     [pickerDisabled]="!selectedDates.length || !!multiDateScheduleError"
                     (pickerValueChange)="onMultiDateTimeChange()"
                     placeholder="Hora de inicio"
                   />
                   <div *ngIf="startTimeMultiCtrl.invalid && (startTimeMultiCtrl.dirty || startTimeMultiCtrl.touched)" class="error-msg">
                     Debes seleccionar una hora de inicio
                   </div>
                 </div>
                 <div class="form-group">
                   <label for="endTimeMulti">Hora de Fin *</label>
                   <input
                     type="text"
                     id="endTimeMulti"
                     #endTimeMultiCtrl="ngModel"
                     [(ngModel)]="newReservaForm.end_time" [afterTime]="newReservaForm.start_time"
                     name="endTimeMulti"
                     required
                     class="form-control"
                     [class.invalid]="endTimeMultiCtrl.invalid && (endTimeMultiCtrl.dirty || endTimeMultiCtrl.touched)"
                     appDatePicker="time"
                     [minTime]="multiDateMinTime"
                     [maxTime]="multiDateMaxTime"
                     [pickerDisabled]="!selectedDates.length || !!multiDateScheduleError"
                     (pickerValueChange)="onMultiDateTimeChange()"
                     placeholder="Hora de fin"
                   />
                   <div *ngIf="endTimeMultiCtrl.invalid && (endTimeMultiCtrl.dirty || endTimeMultiCtrl.touched)" class="error-msg">
                     Debes seleccionar una hora de fin
                   </div>
                 </div>
               </div>
               <div class="info-item" *ngIf="multiDateMode && multiDateEstimatedTotal() > 0">
                 <strong>Total estimado:</strong> {{ '$' + multiDateEstimatedTotal().toFixed(2) }} ({{ selectedDates.length }} día(s))
               </div>

               <!-- Selección de usuario registrado -->
               <div *ngIf="reservationType === 'user'" class="form-group">
                 <label for="userId">Cliente Registrado *</label>
                 <select 
                   id="userId" 
                   #userIdCtrl="ngModel"
                   [(ngModel)]="newReservaForm.user_id" 
                   name="userId"
                   required
                   class="form-control"
                   [class.invalid]="userIdCtrl.invalid && (userIdCtrl.dirty || userIdCtrl.touched)"
                 >
                   <option value="">Seleccionar cliente</option>
                   <option *ngFor="let cliente of customerService.clientes()" [value]="cliente.user_id">
                     {{ cliente.name }} - {{ cliente.email }}
                   </option>
                 </select>
                 <div *ngIf="userIdCtrl.invalid && (userIdCtrl.dirty || userIdCtrl.touched)" class="error-msg">Debes seleccionar un cliente</div>
               </div>

               <!-- Datos de invitado -->
               <div *ngIf="reservationType === 'guest'" class="form-row">
                 <div class="form-group">
                   <label for="guestName">Nombre del Invitado *</label>
                   <input 
                     type="text" 
                     id="guestName" 
                     #guestNameCtrl="ngModel"
                     [(ngModel)]="newReservaForm.guest_name" 
                     name="guestName"
                     required
                     minlength="2"
                     class="form-control"
                     [class.invalid]="guestNameCtrl.invalid && (guestNameCtrl.dirty || guestNameCtrl.touched)"
                     placeholder="Ej: Juan Pérez"
                   />
                   <div *ngIf="guestNameCtrl.invalid && (guestNameCtrl.dirty || guestNameCtrl.touched)" class="error-msg">
                     <span *ngIf="guestNameCtrl.errors?.['required']">El nombre es obligatorio</span>
                     <span *ngIf="guestNameCtrl.errors?.['minlength']">El nombre debe tener al menos 2 caracteres</span>
                   </div>
                 </div>
                 <div class="form-group">
                   <label for="guestPhone">Teléfono *</label>
                   <input 
                     type="tel" 
                     id="guestPhone" 
                     #guestPhoneCtrl="ngModel"
                     [(ngModel)]="newReservaForm.guest_phone" 
                     name="guestPhone"
                     required
                     class="form-control"
                     placeholder="+503 1234-5678"
                   />
                   <div *ngIf="guestPhoneCtrl.invalid && (guestPhoneCtrl.dirty || guestPhoneCtrl.touched)" class="error-msg">El teléfono es obligatorio</div>
                 </div>
               </div>

               <div *ngIf="reservationType === 'guest'" class="form-group">
                 <label for="guestEmail">Email</label>
                 <input 
                   type="email" 
                   id="guestEmail" 
                   #guestEmailCtrl="ngModel"
                   [(ngModel)]="newReservaForm.guest_email" 
                   name="guestEmail"
                   class="form-control"
                   placeholder="juan.perez@example.com"
                 />
                 <div *ngIf="guestEmailCtrl.invalid && (guestEmailCtrl.dirty || guestEmailCtrl.touched)" class="error-msg">Email inválido</div>
               </div>

               <div class="form-group">
                 <label for="numberOfPeople">Número de Personas *</label>
                 <input placeholder="Ej: 4" 
                   type="number" 
                   id="numberOfPeople" 
                   #numPeopleCtrl="ngModel"
                   [(ngModel)]="newReservaForm.number_of_people" 
                   name="numberOfPeople"
                   required
                   min="1"
                   [max]="getSelectedSpaceCapacity()"
                   class="form-control"
                   [class.invalid]="numPeopleCtrl.invalid && (numPeopleCtrl.dirty || numPeopleCtrl.touched)"
                 />
                 <div *ngIf="numPeopleCtrl.invalid && (numPeopleCtrl.dirty || numPeopleCtrl.touched)" class="error-msg">
                   <span *ngIf="numPeopleCtrl.errors?.['required']">Debe indicar el número de personas</span>
                   <span *ngIf="numPeopleCtrl.errors?.['min']">Mínimo 1 persona</span>
                   <span *ngIf="numPeopleCtrl.errors?.['max']">Máximo {{ getSelectedSpaceCapacity() }} personas</span>
                 </div>
               </div>

              <!-- Extras -->
              <div *ngIf="selectedSpaceExtras().length > 0" class="form-group">
                <label>Extras Disponibles</label>
                <div class="extras-list">
                  <ng-container *ngFor="let extra of selectedSpaceExtras()">
                    <div *ngIf="extra.extra_id" class="extra-item">
                      <label class="extra-checkbox">
                        <input 
                          type="checkbox" 
                          [value]="extra.extra_id"
                          (change)="toggleExtra(extra.extra_id!, $event)"
                        />
                        <span>{{ extra.name }} - {{ '$' + extra.price }}</span>
                      </label>
                      <input 
                        *ngIf="isExtraSelected(extra.extra_id)"
                        type="number" 
                        [ngModel]="selectedExtrasMap[extra.extra_id]"
                        (ngModelChange)="updateExtraQuantity(extra.extra_id, $event)"
                        min="1"
                        class="extra-quantity" [attr.max]="extra.max_quantity || null" [attr.data-max-message]="extra.max_quantity ? 'Máximo alcanzado: ' + extra.max_quantity + ' por reserva.' : null"
                        placeholder="Cantidad"
                      />
                    </div>
                  </ng-container>
                </div>
              </div>
              
              <div class="form-actions">
                <button type="button" (click)="closeNewReservaModal()" class="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" class="btn-primary" [disabled]="isCreating() || isLoadingTimeOptions() || availableEndTimes.length === 0" cooldownBubble data-cooldown-key="cal-create-reservation">
                  {{ isCreating() ? 'Creando...' : 'Crear Reserva' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .calendario-container {
      max-width: 1400px;
      margin: 0 auto;
    }
    
    .calendario-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      flex-wrap: wrap;
      gap: 20px;
    }
    
    .calendario-header h2 {
      margin: 0;
      color: var(--ts-bg);
      font-weight: 700;
    }
    
    .view-controls {
      display: flex;
      flex-direction: column;
      gap: 15px;
      align-items: flex-end;
    }
    
    .view-buttons {
      display: flex;
      gap: 5px;
    }
    
    .view-btn {
      background: white;
      color: var(--ts-bg);
      border: 2px solid var(--ts-bg);
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.3s ease;
    }
    
    .view-btn:hover {
      background: var(--ts-bg-gray-50);
    }
    
    .view-btn.active {
      background: var(--ts-bg);
      color: white;
    }
    
    .date-navigation {
      display: flex;
      align-items: center;
      gap: 15px;
    }
    
    .nav-btn {
      background: var(--ts-bg);
      color: white;
      border: 2px solid var(--ts-bg);
      padding: 10px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.3s ease;
    }
    
    .nav-btn:hover {
      background: white;
      color: var(--ts-bg);
    }
    
    .current-date {
      font-size: 18px;
      font-weight: 600;
      color: var(--ts-bg);
      min-width: 200px;
      text-align: center;
    }
    
    .today-btn {
      background: white;
      color: var(--ts-bg);
      border: 2px solid var(--ts-bg);
      padding: 10px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.3s ease;
    }
    
    .today-btn:hover {
      background: var(--ts-bg);
      color: white;
    }
    
    .calendar-grid {
      --calendar-day-header-height: 92px;
      --calendar-hour-height: 60px;
      display: flex;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 10px var(--ts-shadow-gray-1000-a10);
    }
    
    .time-column {
      min-width: 80px;
      background: var(--ts-bg-sky-0);
      border-right: 2px solid var(--ts-border-sky-100);
    }
    
    .time-header {
      box-sizing: border-box;
      height: var(--calendar-day-header-height);
      min-height: var(--calendar-day-header-height);
      display: grid;
      place-items: center;
      padding: 15px;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      text-align: center;
      background: var(--ts-bg-sky-50);
      border-bottom: 2px solid var(--ts-border-sky-100);
    }
    
    .time-slot {
      box-sizing: border-box;
      height: var(--calendar-hour-height);
      min-height: var(--calendar-hour-height);
      padding: 8px;
      border-bottom: 1px solid var(--ts-border-sky-100);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: var(--ts-text-gray-600);
      position: relative;
    }
    
    .salas-columns {
      display: flex;
      flex: 1;
      overflow-x: auto;
    }
    
    .sala-column {
      min-width: 200px;
      border-right: 1px solid var(--ts-border-sky-100);
    }
    
    .sala-column:last-child {
      border-right: none;
    }
    
    .sala-header {
      box-sizing: border-box;
      height: var(--calendar-day-header-height);
      min-height: var(--calendar-day-header-height);
      display: grid;
      grid-template-rows: minmax(0, 1fr) auto;
      align-items: center;
      padding: 15px;
      background: var(--ts-bg-sky-0);
      border-bottom: 2px solid var(--ts-border-sky-100);
      text-align: center;
    }
    
    .sala-header h3 {
      display: -webkit-box;
      align-self: center;
      margin: 0 0 5px 0;
      overflow: hidden;
      font-size: 14px;
      line-height: 1.25;
      color: var(--ts-text-gray-800);
      overflow-wrap: anywhere;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    
    .sala-capacity {
      font-size: 12px;
      color: var(--ts-text-gray-600);
    }
    
    .sala-slots {
      display: flex;
      flex-direction: column;
    }
    
    .time-slot {
      height: 60px;
      border-bottom: 1px solid var(--ts-border-sky-100);
      cursor: pointer;
      transition: background 0.2s ease;
      position: relative;
    }
    
    .time-slot:hover {
      background: var(--ts-bg-sky-0);
    }
    
    .time-slot.reserved {
      cursor: pointer;
    }
    
    .time-slot.confirmed {
      background: var(--ts-bg-green-100);
      border-left: 4px solid var(--ts-border-green-600);
    }
    
    .time-slot.pending {
      background: var(--ts-bg-amber-100);
      border-left: 4px solid var(--ts-border-amber-500);
    }
    
    .time-slot.cancelled {
      background: var(--ts-bg-red-100);
      border-left: 4px solid var(--ts-border-red-450);
    }

    .time-slot.completed {
      background: var(--ts-bg-sky-100);
      border-left: 4px solid var(--ts-border-sky-500);
    }
    
    .time-slot.available {
      cursor: pointer;
      transition: background 0.2s ease;
    }
    
    .time-slot.available:hover {
      background: var(--ts-bg-sky-50-2);
      border-left: 4px solid var(--ts-border-sky-500-2);
    }
    
    .time-slot.available:hover::after {
      content: '+ Agendar';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 10px;
      font-weight: 600;
      color: var(--ts-text-sky-500);
    }
    
    .reserva-info {
      padding: 4px 8px;
      font-size: 11px;
      line-height: 1.3;
    }
    
    .reserva-cliente {
      font-weight: 600;
      color: var(--ts-text-gray-800);
      margin-bottom: 2px;
    }
    
    .reserva-time {
      color: var(--ts-text-gray-600);
      font-size: 10px;
    }
    
.modal-overlay {
       position: fixed;
       top: 0;
       left: 0;
       right: 0;
       bottom: 0;
       background: var(--ts-bg-gray-1000-a75);
       display: flex;
       align-items: center;
       justify-content: center;
       z-index: 1000;
     }
    
    .modal-content {
      background: white;
      border-radius: 12px;
      max-width: 500px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
    }

    .modal-content-large {
       background: white;
       border-radius: 12px;
       max-width: 1100px;
       width: 90%;
       max-height: 90vh;
       overflow-y: auto;
     }

    .info-section {
      background: var(--ts-bg-gray-0-2);
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 20px;
      border: 1px solid var(--ts-border-gray-100);
    }

    .info-item {
      margin-bottom: 8px;
      font-size: 14px;
    }

    .info-item:last-child {
      margin-bottom: 0;
    }

    .radio-group {
      display: flex;
      gap: 24px;
      margin-top: 8px;
    }

    .radio-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 500;
    }

    .radio-label input[type="radio"] {
      cursor: pointer;
    }

    .form-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 16px;
    }

    .extras-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      background: var(--ts-bg-gray-0-2);
      border-radius: 8px;
    }

    .extra-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
    }

    .extra-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      flex: 1;
    }

    .extra-quantity {
      width: 80px;
      padding: 6px 10px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 6px;
      font-size: 14px;
    }

.modal-header {
       display: flex;
       justify-content: space-between;
       align-items: center;
       padding: 20px;
       border-bottom: 1px solid var(--ts-border-sky-100);
       background-color: var(--ts-text);
     }
    
    .modal-header h3 {
      margin: 0;
      color: var(--ts-text-gray-800);
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: var(--ts-text-gray-600);
      padding: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .modal-body {
      padding: 20px;
    }
    
    .modal-actions {
      padding: 0 20px 20px;
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
    .modal-actions .btn-primary,
    .modal-actions .btn-secondary {
      padding: 8px 20px;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      font-size: 14px;
    }
    .modal-actions .btn-primary {
      background: var(--ts-primary, var(--ts-bg));
      color:var(--ts-on-primary);
      border: 2px solid var(--ts-primary, var(--ts-bg));
    }
    .modal-actions .btn-secondary {
      background: transparent;
      color: var(--ts-text-sky-150);
      border: 2px solid var(--ts-divider);
    }

    .reserva-detail {
      margin-bottom: 15px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .reserva-detail strong {
      min-width: 100px;
      color: var(--ts-text-gray-800);
    }
    
    .status-confirmed {
      background: var(--ts-bg-green-100);
      color: var(--ts-text-green-800);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .status-pending {
      background: var(--ts-bg-amber-100);
      color: var(--ts-text-amber-750);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .status-cancelled {
      background: var(--ts-bg-red-100);
      color: var(--ts-text-red-700);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    /* Estilos para vista de semana */
    .week-grid {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 10px var(--ts-shadow-gray-1000-a10);
      border: 2px solid var(--ts-bg);
    }

    .week-header {
      display: flex;
      min-width: fit-content;
      background: var(--ts-bg-gray-50);
      border-bottom: 2px solid var(--ts-bg);
    }

    .week-header-spacer {
      min-width: 80px;
      flex: 0 0 auto;
    }

    .day-header {
      flex: 1 0 140px;
      min-width: 140px;
      padding: 15px;
      text-align: center;
      border-right: 1px solid var(--ts-bg);
    }

    .day-header:last-child {
      border-right: none;
    }

    .day-name {
      font-weight: 600;
      color: var(--ts-bg);
      font-size: 14px;
    }

    .day-date {
      font-size: 12px;
      color: var(--ts-text-gray-800);
      margin-top: 5px;
    }

    .week-content {
      display: flex;
      min-width: fit-content;
    }

    .time-column-week {
      min-width: 80px;
      flex: 0 0 auto;
      background: var(--ts-bg-gray-50);
      border-right: 2px solid var(--ts-bg);
    }

    .week-days {
      display: flex;
      flex: 1;
      min-width: 0;
    }

    .day-column {
      flex: 1 0 140px;
      min-width: 140px;
      border-right: 1px solid var(--ts-bg);
    }

    .day-column:last-child {
      border-right: none;
    }

    .sala-week-column {
      border-bottom: 1px solid var(--ts-bg);
    }

    .sala-week-column:last-child {
      border-bottom: none;
    }

    .sala-week-header {
      padding: 10px;
      background: var(--ts-bg-sky-0);
      font-weight: 600;
      color: var(--ts-bg);
      font-size: 12px;
      text-align: center;
      border-bottom: 1px solid var(--ts-bg);
    }

    .sala-week-slots {
      display: flex;
      flex-direction: column;
    }

    /* Estilos para vista de mes */
    .month-grid {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 10px var(--ts-shadow-gray-1000-a10);
      border: 2px solid var(--ts-bg);
    }

    .month-header {
      display: flex;
      background: var(--ts-bg-gray-50);
      border-bottom: 2px solid var(--ts-bg);
    }

    .month-header .day-name {
      flex: 1;
      padding: 15px;
      text-align: center;
      font-weight: 600;
      color: var(--ts-bg);
      border-right: 1px solid var(--ts-bg);
    }

    .month-header .day-name:last-child {
      border-right: none;
    }

    .month-content {
      display: flex;
      flex-direction: column;
    }

    .month-week {
      display: flex;
      border-bottom: 1px solid var(--ts-bg);
    }

    .month-week:last-child {
      border-bottom: none;
    }

    .month-day {
      flex: 1;
      min-height: 120px;
      padding: 8px;
      border-right: 1px solid var(--ts-bg);
      background: white;
    }

    .month-day:last-child {
      border-right: none;
    }

    .month-day.other-month {
      background: var(--ts-bg-sky-0);
      color: var(--ts-text-gray-400);
    }

    .day-number {
      font-weight: 600;
      color: var(--ts-bg);
      margin-bottom: 5px;
    }

    .day-reservas {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .reserva-month {
      background: var(--ts-bg-gray-50);
      border: 1px solid var(--ts-bg);
      border-radius: 4px;
      padding: 4px;
      cursor: pointer;
      font-size: 10px;
      line-height: 1.2;
    }

    .reserva-month.confirmed {
      background: var(--ts-bg-green-100);
      border-color: var(--ts-border-green-600);
    }

    .reserva-month.pending {
      background: var(--ts-bg-amber-100);
      border-color: var(--ts-border-amber-500);
    }

    .reserva-month.cancelled {
      background: var(--ts-bg-red-100);
      border-color: var(--ts-border-red-450);
    }

    .reserva-month.completed {
      background: var(--ts-bg-sky-100);
      border-color: var(--ts-border-sky-500);
    }

    .reserva-time-month {
      font-weight: 600;
      color: var(--ts-bg);
    }

    .reserva-sala-month {
      color: var(--ts-text-gray-800);
      font-size: 9px;
    }

    .reserva-cliente-month {
      color: var(--ts-text-gray-600);
      font-size: 9px;
    }

    /* Estilos para formulario de nueva reserva */
    .reserva-form {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }
    
    .form-group {
      display: flex;
      flex-direction: column;
    }

    .form-group label {
      margin-bottom: 8px;
      color: var(--ts-bg);
      font-weight: 600;
    }

    .multi-date-toggle{display:flex;align-items:center;gap:.55rem;margin:.85rem 0;padding:.6rem .8rem;color:var(--ts-bg);background:var(--ts-bg-blue-400-a13);border:1px solid var(--ts-border-blue-400-a25);border-radius:9px;font-size:.85rem;font-weight:650;cursor:pointer}
    .multi-date-toggle input{width:16px;height:16px;accent-color:var(--ts-primary)}
    
    .form-control {
      padding: 10px;
      border: 2px solid var(--ts-bg);
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.3s ease;
    }
    
    .form-control:focus {
      outline: none;
      border-color: var(--ts-border-gray-800);
      box-shadow: 0 0 0 2px var(--ts-shadow-gray-1000-a10);
    }
    
.form-actions {
       display: flex;
       gap: 15px;
       margin-top: 20px;
     }
     
     .error-msg {
       color: var(--ts-text-red-300);
       font-size: 12px;
       margin-top: 4px;
       font-weight: 500;
     }
     
     .form-control.invalid {
       border-color: var(--ts-border-red-450);
       box-shadow: 0 0 0 2px var(--ts-shadow-red-450-a25);
     }
    
    .btn-primary {
      background: var(--ts-bg);
      color: white;
      border: 2px solid var(--ts-bg);
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.2s ease;
    }
    
    .btn-primary:hover {
      background: white;
      color: var(--ts-bg);
    }
    
    .btn-secondary {
      background: white;
      color: var(--ts-bg);
      border: 2px solid var(--ts-bg);
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.2s ease;
    }
    
    .btn-secondary:hover {
      background: var(--ts-bg);
      color: white;
    }
    /* Tema TuSala: navegación y eje horario sin estilos negros o claros legacy. */
    .current-date { color: var(--ts-text); }
    .nav-btn {
      color:var(--ts-on-primary);
      background: linear-gradient(135deg, var(--ts-primary), var(--ts-bg-indigo-350));
      border-color: var(--ts-primary-border);
      box-shadow: 0 8px 20px var(--ts-shadow-blue-500-a20);
    }
    .nav-btn:hover { background: linear-gradient(135deg, var(--ts-bg-blue-350), var(--ts-bg-indigo-350-2)); }
    .time-column {
      color: var(--ts-text-muted);
      background: var(--ts-raised);
      border-color: var(--ts-border);
    }

    /* Controles y calendario alineados al Design System oscuro. */
    .calendario-header {
      display: block;
      margin-bottom: 1rem;
      padding: 0;
      background: transparent;
      border: 0;
      box-shadow: none;
    }
    .view-controls {
      width: 100%;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
      gap: 1rem;
    }
    .view-buttons {
      display: inline-grid;
      grid-template-columns: repeat(3, minmax(82px, 1fr));
      align-items: center;
      gap: .35rem;
      padding: .3rem;
      background: var(--ts-bg-elevated);
      border: 1px solid var(--ts-border-strong);
      border-radius: 10px;
    }
    .view-btn {
      min-width: 0;
      height: 40px;
      margin: 0;
      padding: 0 .9rem;
      color: var(--ts-text-blue-300);
      background: transparent;
      border: 1px solid transparent;
      border-radius: 8px;
    }
    .view-btn:hover {
      color: var(--ts-text);
      background: var(--ts-bg-blue-400-a16);
      border-color: var(--ts-border-blue-400-a22);
    }
    .view-btn.active {
      color:var(--ts-on-primary);
      background: linear-gradient(135deg, var(--ts-primary), var(--ts-bg-indigo-350));
      border-color: var(--ts-primary-border-55);
      box-shadow: 0 6px 16px var(--ts-shadow-blue-500-a22);
    }
    .date-navigation {
      min-width: 0;
      display: grid;
      grid-template-columns: auto minmax(180px, 1fr) auto auto;
      align-items: center;
      justify-content: end;
      gap: .65rem;
    }
    .date-navigation :is(.nav-btn, .today-btn) {
      height: 40px;
      min-height: 40px;
      margin: 0;
      padding: 0 .85rem;
      white-space: nowrap;
    }
    .current-date {
      min-width: 0;
      padding: 0 .5rem;
      color: var(--ts-text);
      font-size: 1rem;
      text-align: center;
    }
    .today-btn {
      color: var(--ts-text-sky-150);
      background: var(--ts-bg-elevated);
      border: 1px solid var(--ts-divider);
    }
    .today-btn:hover {
      color: var(--ts-text);
      background: var(--ts-bg-navy-800);
      border-color: var(--ts-border-sky-550);
    }

    .month-grid {
      overflow: hidden;
      color: var(--ts-text-strong);
      background: var(--ts-bg-navy-900);
      border: 1px solid var(--ts-border-strong);
      box-shadow: 0 14px 34px var(--ts-shadow-gray-1000-a20);
    }
    .month-header {
      background: var(--ts-raised);
      border-bottom: 1px solid var(--ts-divider);
    }
    .month-header .day-name {
      color: var(--ts-text-sky-200);
      background: transparent;
      border-right: 1px solid var(--ts-border-sky-300-a16);
    }
    .month-content,
    .month-week {
      background: var(--ts-bg-navy-900);
    }
    .month-week {
      border-bottom: 1px solid var(--ts-border-faint-3);
    }
    .month-day {
      min-width: 0;
      color: var(--ts-text-strong);
      background: var(--ts-bg-elevated);
      border-right: 1px solid var(--ts-border-faint-3);
    }
    .month-day:hover { background: var(--ts-bg-navy-850); }
    .month-day.other-month {
      color: var(--ts-text-sky-550);
      background: var(--ts-bg-navy-900-2);
    }
    .month-day .day-number { color: var(--ts-text); }
    .month-day.other-month .day-number { color: var(--ts-text-sky-550-2); }
    .reserva-month {
      color: var(--ts-text-on-accent-2);
      background: var(--ts-bg-blue-400-a16);
      border: 1px solid var(--ts-border-blue-400-a28);
    }
    .reserva-month.confirmed {
      color: var(--ts-text-green-150);
      background: var(--ts-bg-green-500-a21);
      border-color: var(--ts-border-green-500-a50);
    }
    .reserva-month.pending {
      color: var(--ts-text-amber-200);
      background: var(--ts-bg-orange-450-a21);
      border-color: var(--ts-border-orange-450-a50);
    }
    .reserva-month.cancelled {
      color: var(--ts-text-red-150);
      background: var(--ts-danger-soft);
      border-color: var(--ts-border-red-350-a50);
    }
    .reserva-month.completed {
      color: var(--ts-text-sky-150-2);
      background: var(--ts-bg-blue-350-a16);
      border-color: var(--ts-border-blue-350-a34);
    }
    .reserva-time-month { color: inherit; }
    .reserva-sala-month { color: var(--ts-text-sky-150-3); }
    .reserva-cliente-month { color: var(--ts-text-sky-350); }

    @media (max-width: 1050px) {
      .view-controls { grid-template-columns: 1fr; }
      .view-buttons { justify-self: start; }
      .date-navigation { width: 100%; }
    }
    @media (max-width: 680px) {
      .view-buttons { width: 100%; }
      .date-navigation { grid-template-columns: 1fr 1fr; }
      .current-date { grid-column: 1 / -1; grid-row: 1; }
      .today-btn { grid-column: 1 / -1; }
      .month-grid { overflow:visible; }
      .month-header { display:none; }
      .month-week { display:grid;grid-template-columns:1fr;min-width:0;border-bottom:0;gap:.55rem;margin-bottom:.55rem; }
      .month-day { min-height:78px;padding:.65rem;border:1px solid var(--ts-border-sky-300-a18);border-radius:11px; }
      .month-day.other-month { display:none; }
      .month-day .day-number { display:inline-grid;place-items:center;width:28px;height:28px;border-radius:8px;background:var(--ts-bg-blue-400-a19); }
      .reserva-month { margin-top:.4rem;padding:.45rem .55rem;border-radius:8px; }
    }

    /* --- Dark theme for detail modal --- */
    .modal-content {
      background: var(--ts-bg-elevated);
      border: 1px solid var(--ts-border-strong);
      box-shadow: 0 30px 80px var(--ts-shadow-gray-1000-a42);
    }

    .modal-header.dark-header {
      background: var(--ts-bg-navy-950);
      border-bottom: 2px solid var(--ts-border-navy-750);
    }
    .modal-header.dark-header h3 {
      color: var(--ts-text);
    }
    .modal-header.dark-header .close-btn {
      color: var(--ts-text);
    }

    /* --- Dark theme for new reservation modal --- */
    .modal-content-large {
      width: 96%;
      max-width: 1700px;
      background: var(--ts-bg-elevated);
      border: 1px solid var(--ts-border-strong);
      box-shadow: 0 30px 80px var(--ts-shadow-gray-1000-a42);
    }
    .info-section {
      background: var(--ts-bg-navy-850-2);
      border-color: var(--ts-divider);
    }
    .info-item {
      color: var(--ts-text-sky-150-4);
    }
    .info-item strong {
      color: var(--ts-text-blue-300);
    }
    .reserva-form .form-group label,
    .radio-group .radio-label {
      color: var(--ts-text-sky-150-4);
    }
    .form-control {
      color: var(--ts-text-soft-2);
      background: var(--ts-scrollbar-track);
      border-color: var(--ts-divider);
    }
.form-control:focus {
       border-color: var(--ts-border-blue-400);
       box-shadow: 0 0 0 2px var(--ts-shadow-blue-400-a24);
     }
     .form-control.invalid {
       border-color: var(--ts-border-red-300);
       box-shadow: 0 0 0 2px var(--ts-shadow-red-300-a25);
     }
     .error-msg {
       color: var(--ts-text-red-250);
     }
     .extras-list {
      background: var(--ts-bg-navy-850-2);
      border: 1px solid var(--ts-divider);
    }
    .extra-item span {
      color: var(--ts-text-sky-150-5);
    }
    .extra-quantity {
      color: var(--ts-text-soft-2);
      background: var(--ts-scrollbar-track);
      border-color: var(--ts-divider);
    }
    .modal-header {
      position: sticky;
      top: 0;
      z-index: 3;
      background: var(--ts-bg-navy-950);
      border-bottom: 2px solid var(--ts-border-navy-750);
      box-shadow: 0 2px 12px var(--ts-shadow-gray-1000-a35);
    }
    .modal-header h3 {
      color: var(--ts-text);
    }
    .close-btn {
      color: var(--ts-text);
    }
    .form-actions .btn-primary {
      background: var(--ts-bg-blue-550);
      border: none;
      color:var(--ts-on-primary);
    }
    .form-actions .btn-primary:hover:not(:disabled) {
      background: var(--ts-bg-sky-500);
    }
    .form-actions .btn-secondary {
      color: var(--ts-text-sky-100);
      background: var(--ts-bg-elevated);
      border-color: var(--ts-border-navy-650);
    }
    .form-actions .btn-secondary:hover {
      border-color: var(--ts-border-blue-400);
    }

    /* --- Dark theme for day view (antes solo la vista de mes tenía este
       tratamiento -- día y semana se habían quedado con el fondo blanco
       original, inconsistentes con el resto del panel). Mismos tokens y
       mismos colores de estado (confirmada/pendiente/cancelada) que ya usa
       .reserva-month más arriba, para que las tres vistas se vean como
       parte de un mismo calendario. --- */
    .calendar-grid {
      background: var(--ts-bg-navy-900);
      border: 1px solid var(--ts-border-strong);
      box-shadow: 0 14px 34px var(--ts-shadow-gray-1000-a20);
    }
    .time-header {
      color: var(--ts-text-sky-200);
      background: var(--ts-raised);
      border-bottom-color: var(--ts-divider);
    }
    .time-column .time-slot {
      color: var(--ts-text-sky-350);
      border-bottom-color: var(--ts-border-faint-3);
    }
    .sala-column {
      border-right-color: var(--ts-border-faint-3);
    }
    .sala-header {
      background: var(--ts-raised);
      border-bottom-color: var(--ts-divider);
    }
    .sala-header h3 {
      color: var(--ts-text-strong);
    }
    .sala-capacity {
      color: var(--ts-text-sky-350);
    }
    .salas-columns .time-slot {
      background: var(--ts-bg-elevated);
      border-bottom-color: var(--ts-border-faint-3);
    }
    .salas-columns .time-slot:hover {
      background: var(--ts-bg-navy-850);
    }
    .salas-columns .time-slot.confirmed {
      background: var(--ts-bg-green-500-a21);
      border-left-color: var(--ts-border-green-500);
    }
    .salas-columns .time-slot.pending {
      background: var(--ts-bg-orange-450-a21);
      border-left-color: var(--ts-border-orange-450);
    }
    .salas-columns .time-slot.cancelled {
      background: var(--ts-danger-soft);
      border-left-color: var(--ts-border-red-350);
    }
    .salas-columns .time-slot.completed {
      background: var(--ts-bg-blue-350-a16);
      border-left-color: var(--ts-border-blue-350);
    }
    .salas-columns .time-slot.available:hover {
      background: var(--ts-bg-blue-400-a19);
      border-left-color: var(--ts-border-blue-400-2);
    }
    .salas-columns .time-slot.available:hover::after {
      color: var(--ts-text-blue-250);
    }
    .reserva-cliente {
      color: var(--ts-text-strong);
    }
    .reserva-time {
      color: var(--ts-text-sky-350);
    }

    /* --- Dark theme for week view (mismo motivo que la vista de día) --- */
    .week-grid {
      /* .week-header y .week-content ya no tienen su propio overflow-x
         (harían scroll desincronizado entre sí) -- ambos se desbordan
         juntos dentro de ESTE único contenedor con scroll, así el
         encabezado de días y las columnas de abajo siempre se mueven
         a la par. */
      overflow-x: auto;
      overflow-y: hidden;
      background: var(--ts-bg-navy-900);
      border: 1px solid var(--ts-border-strong);
      box-shadow: 0 14px 34px var(--ts-shadow-gray-1000-a20);
    }
    .week-header,
    .time-column-week {
      background: var(--ts-raised);
      border-color: var(--ts-divider);
    }
    .day-header {
      border-right-color: var(--ts-border-sky-300-a16);
    }
    .day-name {
      color: var(--ts-text-sky-200);
    }
    .day-date {
      color: var(--ts-text-sky-350);
    }
    .day-column {
      border-right-color: var(--ts-border-faint-3);
    }
    .sala-week-column {
      border-bottom-color: var(--ts-border-faint-3);
    }
    .sala-week-header {
      color: var(--ts-text-sky-200);
      background: var(--ts-raised);
      border-bottom-color: var(--ts-divider);
    }
    .week-content .time-slot {
      background: var(--ts-bg-elevated);
      color: var(--ts-text-sky-350);
      border-bottom-color: var(--ts-border-faint-3);
    }
    .week-content .time-slot:hover {
      background: var(--ts-bg-navy-850);
    }
    .week-content .time-slot.confirmed {
      background: var(--ts-bg-green-500-a21);
      border-left-color: var(--ts-border-green-500);
    }
    .week-content .time-slot.pending {
      background: var(--ts-bg-orange-450-a21);
      border-left-color: var(--ts-border-orange-450);
    }
    .week-content .time-slot.cancelled {
      background: var(--ts-danger-soft);
      border-left-color: var(--ts-border-red-350);
    }
    .week-content .time-slot.completed {
      background: var(--ts-bg-blue-350-a16);
      border-left-color: var(--ts-border-blue-350);
    }
    .week-content .time-slot.available:hover {
      background: var(--ts-bg-blue-400-a19);
      border-left-color: var(--ts-border-blue-400-2);
    }
    .week-content .time-slot.available:hover::after {
      color: var(--ts-text-blue-250);
    }

    /* --- Dark theme for detail modal body (reserva-detail / status pills):
       el modal en sí (.modal-content, .modal-header) ya se retemó arriba;
       faltaba el contenido -- texto y badges de estado seguían en negro
       sobre fondo claro dentro de un modal ya oscuro. --- */
    .reserva-detail {
      color: var(--ts-text-sky-150-4);
    }
    .reserva-detail strong {
      color: var(--ts-text-blue-300);
    }
    .status-confirmed {
      color: var(--ts-text-green-150);
      background: var(--ts-bg-green-500-a26);
    }
    .status-pending {
      color: var(--ts-text-amber-200);
      background: var(--ts-bg-orange-450-a26);
    }
    .status-cancelled {
      color: var(--ts-text-red-150);
      background: var(--ts-bg-red-350-a22);
    }
  `]
})
export class CalendarioComponent implements OnInit {
  readonly formatClockTime = formatClockTime;
  @Output() editReservation = new EventEmitter<Reservation>();

  currentDate = signal(new Date());
  selectedReserva = signal<Reservation | null>(null);
  currentView = signal<'dia' | 'semana' | 'mes'>('dia');
  showNewReservaModal = signal(false);
  selectedSala = signal<string>('');
  selectedSpaceId = signal<number | null>(null);
  selectedHour = signal<number>(0);
  selectedDate = signal<Date>(new Date());
  reservationType: 'user' | 'guest' = 'user';
  isCreating = signal(false);
  isLoadingTimeOptions = signal(false);
  // Datos del calendario desde el API (usan 'date' en lugar de 'reservation_date')
  reservations = signal<CalendarReservation[]>([]);
  loading = signal(false);

  // Índice por fecha construido UNA sola vez por carga de datos.
  // El template consulta reservas por día en cada pasada de detección de cambios
  // (hasta 9 veces por celda); si cada consulta hiciera un filter nuevo, se
  // generaba un arreglo nuevo en cada llamada y Angular entraba en un bucle de
  // re-render (decenas de logs idénticos por milisegundo y UI congelada).
  // Este computed solo se recalcula cuando cambia reservations(), por lo que
  // devuelve referencias ESTABLES que cortan el bucle.
  private readonly reservasPorFecha = computed(() => {
    const map = new Map<string, CalendarReservation[]>();
    for (const r of this.reservations()) {
      const arr = map.get(r.date);
      if (arr) {
        arr.push(r);
      } else {
        map.set(r.date, [r]);
      }
    }
    return map;
  });

  horas = Array.from({ length: 24 }, (_, i) => i);
  monthDayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  newReservaForm: any = {
    user_id: null,
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    number_of_people: 1,
    start_time: '',
    end_time: '',
    extras: []
  };
  availableStartTimes: string[] = [];
  availableEndTimes: string[] = [];
  private readonly scheduleCache = new Map<number, SpaceSchedule[]>();

  // Reserva multi-día: mismo espacio y horario aplicado a varios días
  // habilitados, en una sola operación con un solo pago/comprobante.
  multiDateMode = false;
  selectedDates: string[] = [];
  multiDatesInputValue = '';
  multiDateScheduleError: string | null = null;
  multiDateAvailableDays: number[] | undefined;
  multiDateMinTime = '';
  multiDateMaxTime = '';

  get minDateStr(): string {
    return this.formatDateForAPI(new Date());
  }

  selectedExtrasMap: { [key: number]: number } = {};

  @ViewChild('reservaForm') reservaForm!: NgForm;
  @ViewChild('reservaFormEl') reservaFormEl!: ElementRef<HTMLFormElement>;

  selectedSpaceExtras = computed(() => {
    const spaceId = this.selectedSpaceId();
    if (!spaceId) return [];
    
    const espacio = this.salaService.espacios().find(e => e.space_id === spaceId);
    return espacio?.extras || [];
  });

  constructor(
    private salaService: SalaService,
    private reservationService: ReservationService,
    public customerService: CustomerService,
    private authService: AuthService,
    private spaceScheduleService: SpaceScheduleService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    private cooldown: ActionCooldownService,
  ) {}

  ngOnInit(): void {
    // Cargar espacios (una sola vez por sesión: mismo signal compartido que
    // ya refresca sala-detalle tras crear/editar/eliminar un espacio) y
    // datos del calendario.
    if (!this.salaService.espaciosInitialized() && !this.salaService.espaciosLoading()) {
      this.salaService.loadEspacios();
    }
    this.loadCalendarData();
    
    // Cargar clientes
    const businessId = this.getBusinessId();
    if (businessId) {
      this.customerService.loadClientes(businessId);
    }
  }

  getBusinessId(): number | null {
    const business = this.authService.currentUser();
    return business?.id || null;
  }

  onReservationTypeChange(): void {
    if (this.reservationType === 'user') {
      this.newReservaForm.guest_name = '';
      this.newReservaForm.guest_phone = '';
      this.newReservaForm.guest_email = '';
      this.newReservaForm.user_id = null;
    } else {
      this.newReservaForm.user_id = null;
    }
  }

  toggleExtra(extraId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedExtrasMap[extraId] = 1;
    } else {
      delete this.selectedExtrasMap[extraId];
    }
  }

  isExtraSelected(extraId: number): boolean {
    return extraId in this.selectedExtrasMap;
  }

  updateExtraQuantity(extraId: number, value: any): void {
    const numValue = typeof value === 'number' ? value : parseInt(value);
    if (numValue > 0 && !isNaN(numValue)) {
      this.selectedExtrasMap[extraId] = numValue;
    } else {
      delete this.selectedExtrasMap[extraId];
    }
  }

  loadCalendarData(): void {
    this.loading.set(true);
    const date = this.currentDate();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const year = date.getFullYear();
    
    this.salaService.getCalendarData(month, year).subscribe({
      next: (response: CalendarResponse) => {
        this.reservations.set(response.reservations);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error al cargar calendario:', error);
        this.loading.set(false);
      }
    });
  }

  // Semanas/días usados en las vistas 'semana' y 'mes'. DEBEN ser computed:
  // como getters creaban un arreglo NUEVO en cada pasada de detección de
  // cambios, *ngFor detectaba un "cambio" infinito y entraba en un bucle de
  // render que congelaba el navegador (página que no responde).
  readonly weekDays = computed(() => {
    const startOfWeek = this.getStartOfWeek(this.currentDate());
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      return {
        name: this.monthDayNames[date.getDay()],
        date: date.getDate(),
        dateObj: date
      };
    });
  });

  readonly monthWeeks = computed(() => {
    const year = this.currentDate().getFullYear();
    const month = this.currentDate().getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = this.getStartOfWeek(firstDay);
    const weeks = [];
    
    for (let weekStart = new Date(startDate); weekStart <= lastDay; weekStart.setDate(weekStart.getDate() + 7)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        week.push({
          day: day.getDate(),
          dateObj: new Date(day),
          isCurrentMonth: day.getMonth() === month
        });
      }
      weeks.push(week);
    }
    
    return weeks;
  });

  // Convertir espacios del API a formato Sala para compatibilidad.
  // Señal computed declarada UNA sola vez. Usarla como getter que crea un nuevo
  // computhed por acceso (this.salaService.espacios().map) provocaba que en cada
  // pasada de detección de cambios se generara una señal y un arreglo nuevos,
  // entrando en un bucle infinito de render que congelaba el navegador al abrir
  // el calendario.
  readonly salas = computed(() =>
    this.salaService.espacios().map(espacio => ({
      id: espacio.space_id.toString(),
      nombre: (espacio.title || '').trim(),
      capacidad: espacio.capacity,
      descripcion: espacio.description,
      activa: espacio.is_active,
      fechaCreacion: espacio.created_at ? new Date(espacio.created_at) : undefined
    }))
  );

  get reservas() {
    return this.salaService.reservas;
  }

  setView(view: 'dia' | 'semana' | 'mes'): void {
    this.currentView.set(view);
  }

  previousPeriod(): void {
    const oldDate = this.currentDate();
    const newDate = new Date(oldDate);
    if (this.currentView() === 'dia') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (this.currentView() === 'semana') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (this.currentView() === 'mes') {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    this.currentDate.set(newDate);
    
    // Recargar si cambió el mes o año
    if (oldDate.getMonth() !== newDate.getMonth() || oldDate.getFullYear() !== newDate.getFullYear()) {
      this.loadCalendarData();
    }
  }

  nextPeriod(): void {
    const oldDate = this.currentDate();
    const newDate = new Date(oldDate);
    if (this.currentView() === 'dia') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (this.currentView() === 'semana') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (this.currentView() === 'mes') {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    this.currentDate.set(newDate);
    
    // Recargar si cambió el mes o año
    if (oldDate.getMonth() !== newDate.getMonth() || oldDate.getFullYear() !== newDate.getFullYear()) {
      this.loadCalendarData();
    }
  }

  goToToday(): void {
    const oldDate = this.currentDate();
    this.currentDate.set(new Date());
    
    // Recargar si cambió el mes o año
    const newDate = new Date();
    if (oldDate.getMonth() !== newDate.getMonth() || oldDate.getFullYear() !== newDate.getFullYear()) {
      this.loadCalendarData();
    }
  }

  getPreviousText(): string {
    if (this.currentView() === 'dia') return 'Día Anterior';
    if (this.currentView() === 'semana') return 'Semana Anterior';
    if (this.currentView() === 'mes') return 'Mes Anterior';
    return 'Anterior';
  }

  getNextText(): string {
    if (this.currentView() === 'dia') return 'Día Siguiente';
    if (this.currentView() === 'semana') return 'Semana Siguiente';
    if (this.currentView() === 'mes') return 'Mes Siguiente';
    return 'Siguiente';
  }

  getCurrentDateText(): string {
    if (this.currentView() === 'dia') {
      return this.formatDate(this.currentDate());
    } else if (this.currentView() === 'semana') {
      const startOfWeek = this.getStartOfWeek(this.currentDate());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      return `${startOfWeek.getDate()}/${startOfWeek.getMonth() + 1} - ${endOfWeek.getDate()}/${endOfWeek.getMonth() + 1}`;
    } else if (this.currentView() === 'mes') {
      return this.currentDate().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }
    return '';
  }

  getReservaForSalaAndHour(salaId: string, hour: number): CalendarReservation | null {
    const reservasDelDia = this.getReservationsForDate(this.currentDate());
    const sala = this.salas().find(s => s.id === salaId);
    
    if (!sala) {
      return null;
    }
    
    const reserva = reservasDelDia.find(r => {
      // Comparar nombres normalizados (trim y lowercase para evitar problemas con espacios)
      const nombreMatch = r.space_title.trim().toLowerCase() === sala.nombre.trim().toLowerCase();
      const notCancelled = r.status !== 'cancelled';
      
      const [startHour] = r.start_time.split(':').map(Number);
      const [endHour] = r.end_time.split(':').map(Number);
      const hourMatch = hour >= startHour && hour < endHour;
      
      return nombreMatch && notCancelled && hourMatch;
    }) || null;
    
    return reserva;
  }

  showReservaDetails(reserva: CalendarReservation | null): void {
    if (reserva) {
      this.selectedReserva.set(reserva as any);
    }
  }

  closeModal(): void {
    this.selectedReserva.set(null);
  }

  onEditFromDetail(): void {
    const reserva = this.selectedReserva();
    if (reserva) {
      this.editReservation.emit(reserva);
    }
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatHour(hour: number): string {
    return formatClockTime(`${hour.toString().padStart(2, '0')}:00`);
  }

  // Formatear fecha para API (YYYY-MM-DD)
  formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Obtener reservas por día específico (con caché por fecha para evitar
  // recalcular filtros y devolver referencias nuevas en cada pasada de CD).
  private readonly SIN_RESERVAS: CalendarReservation[] = [];
  getReservationsForDate(date: Date): CalendarReservation[] {
    const dateStr = this.formatDateForAPI(date);
    return this.reservasPorFecha().get(dateStr) ?? this.SIN_RESERVAS;
  }

  // Obtener reserva para una hora específica y día
  getReservationForHour(date: Date, hour: number): CalendarReservation | null {
    const reservas = this.getReservationsForDate(date);
    return reservas.find(r => {
      const [h] = r.start_time.split(':').map(Number);
      return h === hour;
    }) || null;
  }

  getEstadoText(estado: string): string {
    const estados = {
      'confirmed': 'Confirmada',
      'pending': 'Pendiente',
      'cancelled': 'Cancelada',
      'completed': 'Completada'
    };
    return estados[estado as keyof typeof estados] || estado;
  }

  getReservaFecha(): string {
    const reserva = this.selectedReserva();
    if (!reserva) return '';
    // La respuesta del calendario usa 'date' en lugar de 'reservation_date'
    const reservationDate = (reserva as any).date || (reserva as any).reservation_date;
    const date = new Date(reservationDate + 'T00:00:00');
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getReservaForSalaAndHourWeek(salaId: string, hour: number, date: Date): CalendarReservation | null {
    const reservasDelDia = this.getReservationsForDate(date);
    const sala = this.salas().find(s => s.id === salaId);
    if (!sala) return null;
    
    return reservasDelDia.find(r => {
      // Comparar nombres normalizados (trim y lowercase para evitar problemas con espacios)
      if (r.space_title.trim().toLowerCase() !== sala.nombre.trim().toLowerCase()) return false;
      if (r.status === 'cancelled') return false;
      
      const [startHour] = r.start_time.split(':').map(Number);
      const [endHour] = r.end_time.split(':').map(Number);
      return hour >= startHour && hour < endHour;
    }) || null;
  }

  getReservasForDay(date: Date): CalendarReservation[] {
    return this.getReservationsForDate(date);
  }

  getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  handleTimeSlotClick(salaId: string, hour: number, date: Date): void {
    // Buscar reserva para el día actual en vista de día
    let reserva: CalendarReservation | null = null;
    if (this.currentView() === 'dia') {
      reserva = this.getReservaForSalaAndHour(salaId, hour);
    } else if (this.currentView() === 'semana') {
      reserva = this.getReservaForSalaAndHourWeek(salaId, hour, date);
    }
    
    if (reserva) {
      // Si hay una reserva, mostrar detalles
      this.showReservaDetails(reserva);
    } else {
      // Si no hay reserva, abrir modal para crear nueva
      this.openNewReservaModal(salaId, hour, date);
    }
  }

  openNewReservaModal(salaId: string, hour: number, date: Date): void {
    this.selectedSala.set(salaId);
    // Convertir salaId a space_id
    const espacio = this.salaService.espacios().find(e => e.space_id.toString() === salaId || e.title.trim().toLowerCase() === salaId.toLowerCase());
    this.selectedSpaceId.set(espacio?.space_id || null);
    this.selectedHour.set(hour);
    this.selectedDate.set(date);
    this.showNewReservaModal.set(true);
    this.reservationType = 'user';
    
    // Resetear formulario
    this.newReservaForm = {
      user_id: null,
      guest_name: '',
      guest_phone: '',
      guest_email: '',
      number_of_people: 1,
      start_time: '',
      end_time: '',
      extras: []
    };
    this.selectedExtrasMap = {};
    this.availableStartTimes = [];
    this.availableEndTimes = [];
    this.multiDateMode = false;
    this.selectedDates = [];
    this.multiDatesInputValue = '';
    this.multiDateScheduleError = null;
    this.multiDateAvailableDays = undefined;
    this.multiDateMinTime = '';
    this.multiDateMaxTime = '';

    if (espacio?.space_id) {
      this.loadAvailableTimeOptions(espacio.space_id, date, this.minutesToTime(hour * 60));
    }
  }

  closeNewReservaModal(): void {
    this.showNewReservaModal.set(false);
    this.selectedSala.set('');
    this.selectedSpaceId.set(null);
    this.selectedHour.set(0);
    this.selectedDate.set(new Date());
    this.availableStartTimes = [];
    this.availableEndTimes = [];
    this.multiDateMode = false;
    this.selectedDates = [];
    this.multiDatesInputValue = '';
    this.multiDateScheduleError = null;
    this.multiDateAvailableDays = undefined;
    this.multiDateMinTime = '';
    this.multiDateMaxTime = '';
  }

  onMultiDateModeChange(): void {
    this.newReservaForm.start_time = '';
    this.newReservaForm.end_time = '';
    this.multiDateScheduleError = null;
    const spaceId = this.selectedSpaceId();

    if (!this.multiDateMode) {
      this.selectedDates = [];
      this.multiDatesInputValue = '';
      this.multiDateAvailableDays = undefined;
      this.multiDateMinTime = '';
      this.multiDateMaxTime = '';
      if (spaceId) {
        this.loadAvailableTimeOptions(spaceId, this.selectedDate(), this.minutesToTime(this.selectedHour() * 60));
      }
      return;
    }

    // Se activa el modo varios días: se parte del día ya elegido en la
    // grilla del calendario, y el admin puede agregar más días desde ahí.
    const seedDate = this.formatDateForAPI(this.selectedDate());
    this.selectedDates = [seedDate];
    this.multiDatesInputValue = seedDate;
    this.availableStartTimes = [];
    this.availableEndTimes = [];
    if (!spaceId) return;
    this.getSchedulesForSpace(spaceId).subscribe({
      next: schedules => {
        const openDays = [0, 1, 2, 3, 4, 5, 6].filter(day => {
          const config = schedules.find(s => s.day_of_week === day);
          return !config || !config.is_closed;
        });
        this.multiDateAvailableDays = schedules.length ? openDays : [0, 1, 2, 3, 4, 5, 6];
        this.recomputeMultiDateWindow(schedules);
      },
      error: () => {
        this.multiDateAvailableDays = [];
        this.multiDateScheduleError = 'No se pudo cargar el horario del espacio.';
      }
    });
  }

  onMultiDatesChange(dates: string[]): void {
    this.selectedDates = dates;
    this.newReservaForm.start_time = '';
    this.newReservaForm.end_time = '';
    this.multiDateScheduleError = null;
    const spaceId = this.selectedSpaceId();
    if (!spaceId || !dates.length) {
      this.multiDateMinTime = '';
      this.multiDateMaxTime = '';
      return;
    }
    this.getSchedulesForSpace(spaceId).subscribe({
      next: schedules => this.recomputeMultiDateWindow(schedules),
      error: () => { this.multiDateScheduleError = 'No se pudo cargar el horario del espacio.'; }
    });
  }

  // Calcula la intersección de horario (apertura/cierre) entre todos los
  // días seleccionados — el mismo rango de hora se aplicará a cada uno. Los
  // choques puntuales de horario (otra reserva ya existente ese día) los
  // valida el backend al crear, todo o nada, igual que en el resto del
  // sistema con reservas multi-día.
  private recomputeMultiDateWindow(schedules: SpaceSchedule[]): void {
    if (!this.selectedDates.length) {
      this.multiDateMinTime = '';
      this.multiDateMaxTime = '';
      return;
    }
    let intersectionStart: number | null = null;
    let intersectionEnd: number | null = null;
    for (const dateStr of this.selectedDates) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const { startMinutes, endMinutes } = this.getOperatingWindowForDate(schedules, date);
      if (startMinutes === null || endMinutes === null) {
        this.multiDateScheduleError = `El espacio no está disponible el ${dateStr} (día cerrado).`;
        this.multiDateMinTime = '';
        this.multiDateMaxTime = '';
        return;
      }
      const currentStart = intersectionStart;
      const currentEnd = intersectionEnd;
      if (currentStart === null || startMinutes > currentStart) intersectionStart = startMinutes;
      if (currentEnd === null || endMinutes < currentEnd) intersectionEnd = endMinutes;
    }
    if (intersectionStart === null || intersectionEnd === null || intersectionEnd - intersectionStart < 30) {
      this.multiDateScheduleError = 'Los días seleccionados no comparten ningún horario en común.';
      this.multiDateMinTime = '';
      this.multiDateMaxTime = '';
      return;
    }
    this.multiDateMinTime = this.minutesToTime(intersectionStart);
    this.multiDateMaxTime = this.minutesToTime(intersectionEnd);
  }

  onMultiDateTimeChange(): void {
    // El picker de hora ya restringe la selección a [multiDateMinTime,
    // multiDateMaxTime]; no se necesita recalcular nada más aquí.
  }

  multiDateEstimatedTotal(): number {
    const spaceId = this.selectedSpaceId();
    const space = this.salaService.espacios().find(e => Number(e.space_id) === Number(spaceId));
    if (!space || !this.newReservaForm.start_time || !this.newReservaForm.end_time) return 0;
    const hours = (this.timeToMinutes(this.newReservaForm.end_time) - this.timeToMinutes(this.newReservaForm.start_time)) / 60;
    if (hours <= 0) return 0;
    return hours * Number(space.price_per_hour) * this.selectedDates.length;
  }

  onStartTimeChange(): void {
    const startTime = this.newReservaForm.start_time;
    if (!startTime) {
      this.availableEndTimes = [];
      this.newReservaForm.end_time = '';
      return;
    }

    this.availableEndTimes = this.buildEndTimeOptions(startTime);
    if (!this.availableEndTimes.includes(this.newReservaForm.end_time)) {
      this.newReservaForm.end_time = this.availableEndTimes[0] || '';
    }
  }

  private loadAvailableTimeOptions(spaceId: number, date: Date, preferredStartTime: string): void {
    this.isLoadingTimeOptions.set(true);
    this.getSchedulesForSpace(spaceId).subscribe({
      next: (schedules) => {
        const { startMinutes, endMinutes } = this.getOperatingWindowForDate(schedules, date);
        if (startMinutes === null || endMinutes === null || endMinutes - startMinutes < 30) {
          this.availableStartTimes = [];
          this.availableEndTimes = [];
          this.isLoadingTimeOptions.set(false);
          this.notifications.error('El espacio no tiene horario habilitado para la fecha seleccionada.');
          return;
        }

        this.availableStartTimes = this.buildStartTimeOptions(spaceId, date, startMinutes, endMinutes);
        if (this.availableStartTimes.length === 0) {
          this.availableEndTimes = [];
          this.newReservaForm.start_time = '';
          this.newReservaForm.end_time = '';
          this.isLoadingTimeOptions.set(false);
          this.notifications.error('No hay franjas horarias disponibles para este espacio en la fecha seleccionada.');
          return;
        }

        const normalizedPreferred = this.availableStartTimes.includes(preferredStartTime)
          ? preferredStartTime
          : this.availableStartTimes[0];
        this.newReservaForm.start_time = normalizedPreferred;
        this.availableEndTimes = this.buildEndTimeOptions(normalizedPreferred);
        this.newReservaForm.end_time = this.availableEndTimes[0] || '';
        this.isLoadingTimeOptions.set(false);
      },
      error: () => {
        this.isLoadingTimeOptions.set(false);
        this.notifications.error('No se pudo cargar el horario del espacio. Intenta nuevamente.');
      }
    });
  }

  private getSchedulesForSpace(spaceId: number): Observable<SpaceSchedule[]> {
    const cached = this.scheduleCache.get(spaceId);
    if (cached) {
      return of(cached);
    }
    return this.spaceScheduleService.getSchedulesBySpace(spaceId).pipe(
      map((response) => {
        const schedules = response?.schedules || [];
        this.scheduleCache.set(spaceId, schedules);
        return schedules;
      })
    );
  }

  private getOperatingWindowForDate(schedules: SpaceSchedule[], date: Date): { startMinutes: number | null; endMinutes: number | null } {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return { startMinutes: 0, endMinutes: 24 * 60 };
    }

    const dayOfWeek = date.getDay();
    const activeForDay = schedules.filter(
      (schedule) => schedule.day_of_week === dayOfWeek && !schedule.is_closed && schedule.opening_time && schedule.closing_time
    );

    if (activeForDay.length === 0) {
      return { startMinutes: null, endMinutes: null };
    }

    const startMinutes = Math.min(...activeForDay.map((schedule) => this.timeToMinutes(schedule.opening_time)));
    const endMinutes = Math.max(...activeForDay.map((schedule) => this.timeToMinutes(schedule.closing_time)));
    return { startMinutes, endMinutes };
  }

  private buildStartTimeOptions(spaceId: number, date: Date, startMinutes: number, endMinutes: number): string[] {
    const options: string[] = [];
    for (let minute = startMinutes; minute <= endMinutes - 30; minute += 30) {
      if (this.isRangeAvailable(spaceId, date, minute, minute + 30)) {
        options.push(this.minutesToTime(minute));
      }
    }
    return options;
  }

  private buildEndTimeOptions(startTime: string): string[] {
    const spaceId = this.selectedSpaceId();
    if (!spaceId) return [];
    const date = this.selectedDate();

    const startMinutes = this.timeToMinutes(startTime);
    const dayLimits = this.getOperatingWindowForDate(this.scheduleCache.get(spaceId) || [], date);
    if (dayLimits.endMinutes === null) return [];

    const options: string[] = [];
    for (let minute = startMinutes + 30; minute <= dayLimits.endMinutes; minute += 30) {
      if (!this.isRangeAvailable(spaceId, date, startMinutes, minute)) {
        break;
      }
      options.push(this.minutesToTime(minute));
    }
    return options;
  }

  private isRangeAvailable(spaceId: number, date: Date, startMinutes: number, endMinutes: number): boolean {
    const targetDate = this.formatDateForAPI(date);
    const conflicts = this.reservations().some((reservation) => {
      if (reservation.status === 'cancelled') return false;
      if (reservation.date !== targetDate) return false;
      if (reservation.space_id !== spaceId) return false;

      const reservationStart = this.timeToMinutes(reservation.start_time);
      const reservationEnd = this.timeToMinutes(reservation.end_time);
      return startMinutes < reservationEnd && endMinutes > reservationStart;
    });
    return !conflicts;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map((value) => parseInt(value, 10));
    return (hours * 60) + minutes;
  }

  private minutesToTime(totalMinutes: number): string {
    const normalizedMinutes = Math.max(0, Math.min(totalMinutes, (24 * 60) - 1));
    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

createReserva(): void {
     if (!this.cooldown.consume('cal-create-reservation')) return;
     if (!validateAndFocusFirstError(this.reservaForm, this.reservaFormEl?.nativeElement, msg => this.notifications.error(msg), 'Complete todos los campos requeridos')) return;

     const spaceId = this.selectedSpaceId();
     const fecha = this.selectedDate();
     const horaInicioStr = String(this.newReservaForm.start_time || '').trim();
     const horaFinStr = String(this.newReservaForm.end_time || '').trim();

    if (!spaceId) {
      this.notifications.error('Debes seleccionar un espacio');
      return;
    }

    const selectedSpace = this.salaService.espacios().find(space => Number(space.space_id) === Number(spaceId));
    const requestedPeople = Number(this.newReservaForm.number_of_people);
    if (!selectedSpace || !Number.isInteger(requestedPeople) || requestedPeople < 1 || requestedPeople > Number(selectedSpace.capacity)) {
      this.notifications.error(`La cantidad debe estar entre 1 y ${selectedSpace?.capacity || 1} personas`);
      return;
    }

    if (!horaInicioStr || !horaFinStr) {
      this.notifications.error('Debes seleccionar un horario válido');
      return;
    }

    if (this.timeToMinutes(horaInicioStr) >= this.timeToMinutes(horaFinStr)) {
      this.notifications.error('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    if (this.reservationType === 'user' && !this.newReservaForm.user_id) {
      this.notifications.error('Debes seleccionar un cliente');
      return;
    }

    if (this.reservationType === 'guest' && (!this.newReservaForm.guest_name || !this.newReservaForm.guest_phone)) {
      this.notifications.error('Debes completar los datos del invitado');
      return;
    }

    // Preparar extras
    const extras = Object.keys(this.selectedExtrasMap).map(extraId => ({
      extra_id: parseInt(extraId),
      quantity: this.selectedExtrasMap[parseInt(extraId)]
    }));

    if (this.multiDateMode) {
      if (!this.selectedDates.length) {
        this.notifications.error('Selecciona al menos un día para reservar.');
        return;
      }
      if (this.multiDateScheduleError) {
        this.notifications.error(this.multiDateScheduleError);
        return;
      }
      // Sin pre-chequeo de disponibilidad (solo acepta una fecha) — la
      // validación real, transaccional y todo-o-nada la hace el backend al
      // crear, igual que en el resto del sistema con reservas multi-día.
      this.isCreating.set(true);
      this.submitReservaMultiDate(spaceId, this.selectedDates, horaInicioStr, horaFinStr, extras);
      return;
    }

    this.isCreating.set(true);

    // Formatear fecha
    const fechaStr = this.formatDateForAPI(fecha);

    this.reservationService.verificarDisponibilidad(
      spaceId,
      fechaStr,
      horaInicioStr,
      horaFinStr,
      this.newReservaForm.number_of_people
    ).subscribe({
      next: (availability) => {
        if (!availability.available) {
          this.isCreating.set(false);
          this.notifications.error(availability.message || 'El horario seleccionado ya no está disponible.');
          return;
        }
        this.submitReserva(spaceId, fechaStr, horaInicioStr, horaFinStr, extras);
      },
      error: (error) => {
        this.isCreating.set(false);
        const errorMessage = friendlyError(error, 'No se pudo validar la disponibilidad del horario');
        this.notifications.error(errorMessage);
      }
    });
  }

  private async submitReserva(
    spaceId: number,
    fechaStr: string,
    horaInicioStr: string,
    horaFinStr: string,
    extras: Array<{ extra_id: number; quantity: number }>
  ): Promise<void> {
    const action$ = this.reservationType === 'user'
      ? this.reservationService.crearReservacionParaUsuario({
          user_id: this.newReservaForm.user_id,
          space_id: spaceId,
          reservation_date: fechaStr,
          start_time: horaInicioStr,
          end_time: horaFinStr,
          number_of_people: this.newReservaForm.number_of_people,
          extras: extras.length > 0 ? extras : undefined
        } as CreateReservationForUserRequest)
      : this.reservationService.crearReservacionParaInvitado({
          space_id: spaceId,
          reservation_date: fechaStr,
          start_time: horaInicioStr,
          end_time: horaFinStr,
          number_of_people: this.newReservaForm.number_of_people,
          guest_name: this.newReservaForm.guest_name,
          guest_phone: this.newReservaForm.guest_phone,
          guest_email: this.newReservaForm.guest_email || undefined,
          extras: extras.length > 0 ? extras : undefined
        } as CreateReservationForGuestRequest);

    try {
      const reserva = await this.dialogs.confirmAndRun(
        { title: 'Confirmar reservación', message: '¿Confirmas que deseas crear esta reservación?', tone: 'info', confirmText: 'Crear reservación' },
        () => action$,
        'Reservación creada exitosamente',
        error => friendlyError(error, 'Error al crear la reservación'),
      );
      if (reserva) {
        this.closeNewReservaModal();
        this.loadCalendarData();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isCreating.set(false);
    }
  }

  private async submitReservaMultiDate(
    spaceId: number,
    dates: string[],
    horaInicioStr: string,
    horaFinStr: string,
    extras: Array<{ extra_id: number; quantity: number }>
  ): Promise<void> {
    const action$ = this.reservationType === 'user'
      ? this.reservationService.crearReservacionParaUsuario({
          user_id: this.newReservaForm.user_id,
          space_id: spaceId,
          reservation_dates: dates,
          start_time: horaInicioStr,
          end_time: horaFinStr,
          number_of_people: this.newReservaForm.number_of_people,
          extras: extras.length > 0 ? extras : undefined
        } as CreateReservationForUserRequest)
      : this.reservationService.crearReservacionParaInvitado({
          space_id: spaceId,
          reservation_dates: dates,
          start_time: horaInicioStr,
          end_time: horaFinStr,
          number_of_people: this.newReservaForm.number_of_people,
          guest_name: this.newReservaForm.guest_name,
          guest_phone: this.newReservaForm.guest_phone,
          guest_email: this.newReservaForm.guest_email || undefined,
          extras: extras.length > 0 ? extras : undefined
        } as CreateReservationForGuestRequest);

    try {
      const reserva = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar reservación',
          message: `¿Confirmas que deseas crear esta reservación para ${dates.length} días? Se generará un solo pago y un solo comprobante con el detalle de todas las fechas.`,
          tone: 'info', confirmText: 'Crear reservación',
        },
        () => action$,
        `Reservación de ${dates.length} días creada exitosamente`,
        error => friendlyError(error, 'Error al crear la reservación'),
      );
      if (reserva) {
        this.closeNewReservaModal();
        this.loadCalendarData();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isCreating.set(false);
    }
  }

  getSalaNombre(salaId: string): string {
    const sala = this.salas().find(s => s.id === salaId);
    return sala ? sala.nombre : '';
  }

  getEspacioNombre(spaceId: number | null): string {
    if (!spaceId) return '';
    const espacio = this.salaService.espacios().find(e => e.space_id === spaceId);
    return espacio ? espacio.title : '';
  }

  getSelectedSpaceCapacity(): number {
    const selected = this.salaService.espacios().find(space => Number(space.space_id) === Number(this.selectedSpaceId()));
    return Math.max(1, Number(selected?.capacity || 1));
  }

}
